import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { invalidateSeoCache, applySeo } from "@/hooks/useSeo";
import { Upload, Trash2, Loader2, Save, ExternalLink, Image as ImageIcon } from "lucide-react";

export default function AdminSeo() {
    const [seo, setSeo] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [msg, setMsg] = useState(null);

    const load = async () => {
        const r = await apiPrivate.get("/seo");
        setSeo(r.data);
        setForm({
            site_title: r.data.site_title,
            site_description: r.data.site_description,
            site_keywords: r.data.site_keywords,
            og_title: r.data.og_title,
            og_description: r.data.og_description,
            twitter_handle: r.data.twitter_handle,
        });
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true); setMsg(null);
        try {
            const r = await apiPrivate.patch("/admin/seo", form);
            setSeo(r.data);
            invalidateSeoCache();
            applySeo(r.data);
            setMsg({ type: "ok", text: "Impostazioni salvate. I tag SEO sono già applicati." });
        } catch (e) {
            setMsg({ type: "err", text: e?.response?.data?.detail || "Errore" });
        } finally { setSaving(false); }
    };

    const uploadImage = async (file) => {
        if (!file) return;
        setUploading(true); setMsg(null);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await apiPrivate.post("/admin/seo/og-image", fd, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            setSeo(r.data);
            invalidateSeoCache();
            applySeo(r.data);
            setMsg({ type: "ok", text: "Immagine caricata. Cache scraper social (Facebook/X/LinkedIn) può impiegare alcuni minuti per aggiornarsi." });
        } catch (e) {
            setMsg({ type: "err", text: e?.response?.data?.detail || "Errore upload" });
        } finally { setUploading(false); }
    };

    const removeImage = async () => {
        if (!window.confirm("Rimuovere l'immagine OG?")) return;
        const r = await apiPrivate.delete("/admin/seo/og-image");
        setSeo(r.data);
        invalidateSeoCache();
        applySeo(r.data);
    };

    if (!seo) return <AppLayout admin><div className="text-zinc-500">Caricamento…</div></AppLayout>;

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-seo">
                <span className="label-tag text-magenta">SEO & Social</span>
                <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tighter">Identità <span className="font-serif-italic text-acid">digitale</span>.</h1>
                <p className="text-zinc-400 mt-4 max-w-2xl">Imposta i metadati che Google, Facebook, X (Twitter), LinkedIn e altri scraper usano quando il link della piattaforma viene condiviso o indicizzato.</p>

                <div className="bento p-5 mt-6 border-acid/30 bg-acid/5" data-testid="share-info">
                    <div className="flex flex-wrap items-center gap-4 justify-between">
                        <div className="text-sm font-grotesk text-zinc-300 max-w-2xl">
                            <span className="text-acid font-bold">💡 Per condividere con preview aggiornata dinamicamente</span> su WhatsApp/Facebook/LinkedIn (che non eseguono JS), usa questo link invece di quello standard:
                        </div>
                        <code className="bg-[#0F0F0F] border border-zinc-800 px-3 py-2 font-mono text-acid text-xs select-all" data-testid="share-url">
                            {window.location.origin}/api/share
                        </code>
                    </div>
                </div>

                {msg && (
                    <div className={`mt-6 px-4 py-3 text-sm font-grotesk ${msg.type === "ok" ? "border border-acid/40 bg-acid/5 text-acid" : "border border-magenta/40 bg-magenta/10 text-magenta"}`} data-testid="seo-msg">
                        {msg.text}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
                    {/* FORM */}
                    <div className="lg:col-span-2 space-y-6">
                        <Section title="SEO motori di ricerca">
                            <Field label="Titolo (≤ 60 caratteri consigliati)" v={form.site_title} on={v => setForm(s => ({ ...s, site_title: v }))} max={120} testid="seo-title" />
                            <Field label="Description (≤ 160 caratteri consigliati)" v={form.site_description} on={v => setForm(s => ({ ...s, site_description: v }))} max={320} multiline testid="seo-description" />
                            <Field label="Keywords (separate da virgola)" v={form.site_keywords} on={v => setForm(s => ({ ...s, site_keywords: v }))} max={320} testid="seo-keywords" />
                        </Section>

                        <Section title="Open Graph / Social sharing">
                            <Field label="OG Title (Facebook, LinkedIn, WhatsApp, Telegram)" v={form.og_title} on={v => setForm(s => ({ ...s, og_title: v }))} max={120} testid="og-title" />
                            <Field label="OG Description" v={form.og_description} on={v => setForm(s => ({ ...s, og_description: v }))} max={320} multiline testid="og-description" />
                            <Field label="X (Twitter) handle (es. @startupitalia)" v={form.twitter_handle} on={v => setForm(s => ({ ...s, twitter_handle: v }))} max={40} testid="twitter-handle" />
                        </Section>

                        <Section title="Immagine di sharing (Open Graph)">
                            <div className="space-y-4">
                                {seo.og_image_url ? (
                                    <div className="bento p-4 flex items-center gap-4">
                                        <img src={seo.og_image_url + "?v=" + (seo.updated_at || "")} alt="OG" className="w-40 h-auto border border-zinc-800" data-testid="og-image-preview" />
                                        <div className="flex-1 text-sm text-zinc-400 font-grotesk">
                                            <div className="text-white">Immagine attiva</div>
                                            <a href={seo.og_image_url} target="_blank" rel="noreferrer" className="text-acid inline-flex items-center gap-1 mt-1 text-xs uppercase tracking-widest" data-testid="og-image-link">
                                                Apri URL <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </div>
                                        <button onClick={removeImage} className="p-2 border border-zinc-800 hover:border-magenta hover:text-magenta" data-testid="og-image-remove">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-sm text-zinc-500 font-grotesk">Nessuna immagine caricata.</div>
                                )}
                                <label htmlFor="og-file" className="block border-2 border-dashed border-zinc-800 p-8 text-center cursor-pointer hover:border-acid transition" data-testid="og-dropzone">
                                    <Upload className="h-6 w-6 mx-auto text-zinc-500" />
                                    <div className="mt-3 font-grotesk text-sm">
                                        {uploading ? "Caricamento…" : "PNG / JPEG / WebP — dimensione consigliata 1200×630 — max 5MB"}
                                    </div>
                                </label>
                                <input id="og-file" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} data-testid="og-file-input" />
                            </div>
                        </Section>

                        <button onClick={save} disabled={saving} className="btn-rebel" data-testid="seo-save">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Salva metadati testuali
                        </button>
                    </div>

                    {/* PREVIEW */}
                    <aside className="space-y-4">
                        <div className="label-tag mb-3">— Anteprima social card</div>
                        <div className="bento p-0 overflow-hidden" data-testid="social-preview">
                            {seo.og_image_url ? (
                                <img src={seo.og_image_url + "?v=" + (seo.updated_at || "")} alt="OG" className="w-full aspect-[1200/630] object-cover bg-zinc-900" />
                            ) : (
                                <div className="w-full aspect-[1200/630] bg-zinc-900 flex items-center justify-center text-zinc-600">
                                    <ImageIcon className="h-12 w-12" />
                                </div>
                            )}
                            <div className="p-4 bg-[#080808]">
                                <div className="text-[10px] uppercase tracking-widest font-grotesk text-zinc-500 truncate">{window.location.host}</div>
                                <div className="font-grotesk font-semibold text-white mt-1 line-clamp-2">{form.og_title || form.site_title}</div>
                                <div className="text-zinc-400 text-sm mt-1 line-clamp-2">{form.og_description || form.site_description}</div>
                            </div>
                        </div>

                        <div className="bento p-4" data-testid="google-preview">
                            <div className="label-tag mb-2">— Anteprima Google</div>
                            <div className="text-xs text-zinc-500">{window.location.host}</div>
                            <div className="text-[#8ab4f8] text-lg mt-1 line-clamp-1 font-grotesk">{form.site_title}</div>
                            <div className="text-zinc-400 text-sm mt-1 line-clamp-2">{form.site_description}</div>
                        </div>

                        <div className="bento p-4 text-xs font-grotesk text-zinc-500 leading-relaxed">
                            💡 Per aggiornare le anteprime già in cache su Facebook/LinkedIn, usa rispettivamente <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noreferrer" className="text-acid">Sharing Debugger</a> e <a href="https://www.linkedin.com/post-inspector/" target="_blank" rel="noreferrer" className="text-acid">Post Inspector</a> per forzare il refresh.
                        </div>
                    </aside>
                </div>
            </div>
        </AppLayout>
    );
}

function Section({ title, children }) {
    return (
        <div className="bento p-8">
            <div className="label-tag mb-5">— {title}</div>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

function Field({ label, v, on, multiline, max, testid }) {
    const count = (v || "").length;
    return (
        <div>
            <div className="flex justify-between items-baseline mb-2">
                <span className="label-tag">{label}</span>
                {max && <span className="text-[10px] font-mono text-zinc-600">{count}/{max}</span>}
            </div>
            {multiline ? (
                <textarea rows={3} value={v || ""} onChange={(e) => on(e.target.value)} maxLength={max} data-testid={testid} className="w-full bg-[#0F0F0F] border border-zinc-800 text-white p-3 font-grotesk focus:outline-none focus:border-acid resize-none" />
            ) : (
                <input value={v || ""} onChange={(e) => on(e.target.value)} maxLength={max} data-testid={testid} className="w-full bg-[#0F0F0F] border border-zinc-800 text-white px-3 py-2 font-grotesk focus:outline-none focus:border-acid" />
            )}
        </div>
    );
}
